import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { IconSparkles, IconTrash, IconX } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import templateSetService from '../../services/templateSetService';
import { useTranslation } from '../../i18n/hooks/useTranslation';

// Structural markers that aren't real placeholder fields — skip in the modal.
const SKIP_TYPES = new Set(['Steps', 'Separator']);

export default function PreviewValuesModal({ opened, onClose, templateSet, onSaved }) {
    const { t } = useTranslation();
    // Shape: { [docId]: { [slug]: value } }
    const [docValues, setDocValues] = useState({});
    const [activeDocId, setActiveDocId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    const placeholders = (templateSet?.placeholders || []).filter(p => !SKIP_TYPES.has(p.type));
    const docs = templateSet?.docs || [];

    useEffect(() => {
        if (!opened) return;
        // Seed values map from each doc's saved previewValues
        const seeded = {};
        for (const d of docs) {
            seeded[d.id] = { ...(d.previewValues || {}) };
        }
        setDocValues(seeded);
        setActiveDocId(docs[0]?.id ? String(docs[0].id) : null);
    }, [opened, templateSet?.id]);

    const handleChange = (docId, slug, v) => {
        setDocValues(prev => ({
            ...prev,
            [docId]: { ...(prev[docId] || {}), [slug]: v },
        }));
    };

    const handleAiFill = async () => {
        if (!templateSet?.id || !placeholders.length) return;
        setAiLoading(true);
        try {
            const response = await templateSetService.generateAiPreviewValues(
                templateSet.id,
                placeholders.map(p => ({
                    slug: p.slug,
                    title: p.title,
                    type: p.type,
                    description: p.description,
                }))
            );
            const values = response?.data ?? response ?? {};
            if (!Object.keys(values).length) {
                notifications.show({ title: 'AI', message: 'কোনো মান তৈরি হয়নি।', color: 'orange' });
                return;
            }
            // Fill all docs with the AI values (same values across docs)
            setDocValues(prev => {
                const next = { ...prev };
                for (const d of docs) {
                    next[d.id] = { ...(next[d.id] || {}), ...values };
                }
                return next;
            });
            notifications.show({ title: 'AI', message: 'সকল ফিল্ড AI দিয়ে পূরণ হয়েছে।', color: 'green' });
        } catch (err) {
            notifications.show({ title: 'AI Error', message: err.message || 'AI দিয়ে পূরণ করতে ব্যর্থ হয়েছে।', color: 'red' });
        } finally {
            setAiLoading(false);
        }
    };

    const handleSave = async () => {
        if (!templateSet?.id) return;
        setSaving(true);
        try {
            // Per doc: keep only current-placeholder slugs with non-empty trimmed strings.
            const payload = {};
            for (const d of docs) {
                const raw = docValues[d.id] || {};
                const trimmed = {};
                for (const p of placeholders) {
                    const v = raw[p.slug];
                    if (typeof v === 'string' && v.trim() !== '') {
                        trimmed[p.slug] = v;
                    }
                }
                payload[d.id] = trimmed;
            }

            await templateSetService.updatePreviewValues(templateSet.id, payload);
            notifications.show({
                title: t('common.success'),
                message: t('templateSets.previewValuesSaved'),
                color: 'green',
            });
            onSaved?.(payload);
            onClose?.();
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err.message || t('templateSets.failedToSavePreviewValues'),
                color: 'red',
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={opened} onOpenChange={(open) => { if (!open) onClose?.(); }}>
            <DialogContent className="max-w-2xl w-full">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <span className="font-semibold">{t('templateSets.dummyPreviewValues')}</span>
                        {templateSet?.name && (
                            <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                                {templateSet.name}
                            </span>
                        )}
                    </DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    {docs.length === 0 || placeholders.length === 0 ? (
                        <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-6">
                            {t('templateSets.noPlaceholdersToFill')}
                        </p>
                    ) : (
                        <>
                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                {t('templateSets.dummyPreviewValuesDescription')}
                            </p>

                            {/* Doc tab switcher */}
                            {docs.length > 1 && (
                                <div className="flex border-b border-zinc-200 dark:border-zinc-700 gap-0.5 overflow-x-auto">
                                    {docs.map((d, i) => (
                                        <button
                                            key={d.id}
                                            className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${activeDocId === String(d.id)
                                                ? 'text-orange-600 border-b-2 border-orange-500 -mb-px'
                                                : 'text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100'
                                                }`}
                                            onClick={() => setActiveDocId(String(d.id))}
                                        >
                                            {d.title || `Document ${i + 1}`}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Active doc fields */}
                            {docs.map(d => (
                                activeDocId === String(d.id) && (
                                    <div key={d.id} className="overflow-y-auto" style={{ maxHeight: '55vh' }}>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pr-1">
                                            {placeholders.map(p => (
                                                <div key={p.id} className="flex flex-col gap-1">
                                                    <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 flex items-center gap-1.5 flex-wrap">
                                                        {p.title}
                                                        <span className="px-1.5 py-0.5 text-xs rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">{p.type}</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="w-full px-3 py-2 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                        placeholder={t('templateSets.dummyValueFor', { title: p.title })}
                                                        value={(docValues[d.id]?.[p.slug]) || ''}
                                                        onChange={(e) => handleChange(d.id, p.slug, e.target.value)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            ))}
                        </>
                    )}

                    {/* Footer actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-zinc-200 dark:border-zinc-700">
                        <div className="flex items-center gap-2">
                            <button
                                title="AI দিয়ে সকল ফিল্ড স্বয়ংক্রিয়ভাবে পূরণ করুন"
                                className="px-3 py-1.5 text-sm rounded-md border border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
                                onClick={handleAiFill}
                                disabled={aiLoading || saving || !placeholders.length}
                            >
                                {aiLoading ? (
                                    <span className="w-3 h-3 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <IconSparkles size={16} />
                                )}
                                AI দিয়ে পূরণ করুন
                            </button>
                            <button
                                title="সব ফিল্ড খালি করুন"
                                className="px-3 py-1.5 text-sm rounded-md border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
                                disabled={saving || aiLoading}
                                onClick={() => {
                                    const cleared = {};
                                    for (const d of docs) {
                                        cleared[d.id] = {};
                                    }
                                    setDocValues(cleared);
                                }}
                            >
                                <IconTrash size={16} />
                                Clear
                            </button>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                className="px-3 py-1.5 text-sm rounded-md border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
                                onClick={onClose}
                                disabled={saving || aiLoading}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                className="px-3 py-1.5 text-sm rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
                                onClick={handleSave}
                                disabled={saving || aiLoading}
                            >
                                {saving && <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                                {t('common.save')}
                            </button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
